import type { CSSProperties, ElementType, ForwardedRef } from 'react';
import { forwardRef } from 'react';

import {
  radius as radiusValues,
  spacing as spacingValues,
} from 'styles/config/spacing';
import { radiusToStyle } from 'styles/utils';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';
import type { StackComponentProps } from './types';

const TOKENS = { ...spacingValues, ...radiusValues } as Record<string, string>;
const SPACING_THEME = { spacing: TOKENS } as any;

const ALIGN: Record<string, string> = {
  stretch: 'items-stretch',
  center: 'items-center',
  'flex-start': 'items-start',
  'flex-end': 'items-end',
  baseline: 'items-baseline',
  start: 'items-start',
  end: 'items-end',
};

const JUSTIFY: Record<string, string> = {
  'flex-start': 'justify-start',
  'flex-end': 'justify-end',
  center: 'justify-center',
  'space-between': 'justify-between',
  'space-around': 'justify-around',
  'space-evenly': 'justify-evenly',
  start: 'justify-start',
  end: 'justify-end',
};

const DIRECTION: Record<string, string> = {
  row: 'flex-row',
  'row-reverse': 'flex-row-reverse',
  column: 'flex-col',
  'column-reverse': 'flex-col-reverse',
};

const OVERFLOW: Record<string, string> = {
  visible: 'overflow-visible',
  hidden: 'overflow-hidden',
  scroll: 'overflow-scroll',
  auto: 'overflow-auto',
  clip: 'overflow-clip',
};

const WRAP: Record<string, string> = {
  wrap: 'flex-wrap',
  nowrap: 'flex-nowrap',
  'wrap-reverse': 'flex-wrap-reverse',
};

function toCssValue(value: string | number): string {
  return typeof value === 'number' ? `${value}px` : value;
}

type StackProps = Partial<StackComponentProps> & { as?: ElementType };

function buildDynamicStyle(props: StackProps): CSSProperties {
  const {
    flex = '0 1 auto',
    spacing = 'none',
    width,
    height,
    maxWidth,
    maxHeight,
    background,
    borderColor,
    borderWidth,
    radius = 'none',
    flow,
    style,
  } = props;

  const resolvedRadius =
    radius !== 'none' ? radiusToStyle(SPACING_THEME, radius as any) : undefined;

  return {
    ...(flex !== undefined && { flex }),
    ...(spacing !== 'none' && { gap: TOKENS[spacing] }),
    ...(width !== undefined && { width: toCssValue(width) }),
    ...(height !== undefined && { height: toCssValue(height) }),
    ...(maxWidth !== undefined && { maxWidth: toCssValue(maxWidth) }),
    ...(maxHeight !== undefined && { maxHeight: toCssValue(maxHeight) }),
    ...(background && { backgroundColor: colorCssVar(background) }),
    ...(borderColor && { borderColor: colorCssVar(borderColor) }),
    ...(borderWidth !== undefined && { borderWidth }),
    ...(resolvedRadius &&
      resolvedRadius !== '0' && { borderRadius: resolvedRadius }),
    ...(flow && { flexFlow: flow }),
    ...style,
  };
}

/** @deprecated Use plain HTML elements with Tailwind utility classes instead. */
export const Stack = forwardRef(
  (
    {
      as,
      children,
      align = 'stretch',
      borderColor,
      borderStyle,
      borderWidth,
      direction,
      display = 'flex',
      justify = 'flex-start',
      overflow = 'initial',
      position,
      wrap,
      className,
      ...rest
    }: StackProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const Component = (as || 'div') as ElementType;

    const classes = cn(
      display === 'inline-flex' ? 'inline-flex' : 'flex',
      align !== undefined && ALIGN[align],
      justify !== undefined && JUSTIFY[justify],
      direction !== undefined && DIRECTION[direction],
      wrap !== undefined && WRAP[wrap],
      overflow !== undefined && overflow !== 'initial' && OVERFLOW[overflow],
      position !== undefined && position,
      (borderWidth || borderColor) &&
        (borderStyle ? `border-${borderStyle}` : 'border-solid'),
      className,
    );

    return (
      <Component
        ref={ref}
        className={classes}
        style={buildDynamicStyle({ borderColor, borderWidth, ...rest })}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

Stack.displayName = 'Stack';
